import type { ClientPlaygroundConfigOptions, MockClient } from '@gotti/test-utils';
import type { PlaygroundRuntimeSelection } from '@gotti/playground/browser';

type ClientLayer = ClientPlaygroundConfigOptions;
type CreatedHook = NonNullable<ClientLayer['onClientCreated']>;

/**
 * The two layers "+ Add Client" hands `environment.createClients(1, definition, config)` so the new client is made the
 * way the mounted definition makes its own: the definition's `clientConfig` over the config's, as the playground's
 * default setup layers them (one object declared at both levels is one layer), as client `index` of the definition —
 * the preview numbers the clients it adds after the ones the definition booted. test-utils numbers a `createClients`
 * call's clients from 0, so each layer's per-client fields (`clientEnv`, `clientAuth`, `onClientCreated`, a
 * `joinGame`'s `options` and `initialAreas`) are asked about `index` instead. The client draws on the surface
 * `surface()` gives as it is created, whatever either layer's `domSetup` or `layout` says.
 *
 * An offline environment is refused: its clients join at boot, through `offline.initAreas`, which a client added later
 * does not go through.
 */
export function definitionClientLayers(
  selection: PlaygroundRuntimeSelection,
  index: number,
  surface: () => { canvas: HTMLCanvasElement; container: HTMLElement },
): { definition: ClientLayer; config: ClientLayer | undefined } {
  if (selection.environment?.offline) {
    throw new Error(
      `[Sandbox] Playground "${selection.name}" is offline: its clients join at boot, through \`offline.initAreas\`, ` +
        'so "+ Add Client" cannot add one the way it does.',
    );
  }
  // A hook declared at both levels runs once, as at boot: both layers get the same wrapper.
  const created = new Map<CreatedHook, CreatedHook>();
  const atIndex = (layer: ClientLayer): ClientLayer => {
    const { clientEnv, clientAuth, onClientCreated, joinGame, domSetup: _domSetup, layout: _layout, ...rest } = layer;
    const shifted: ClientLayer = { ...rest };
    if (clientEnv !== undefined) {
      shifted.clientEnv = typeof clientEnv === 'function' ? (_first: number, previous?: unknown) => clientEnv(index, previous) : clientEnv;
    }
    if (clientAuth !== undefined) {
      shifted.clientAuth = typeof clientAuth === 'function' ? (param: { index: number }) => clientAuth({ ...param, index }) : clientAuth;
    }
    if (onClientCreated) {
      if (!created.has(onClientCreated)) {
        created.set(onClientCreated, (client: MockClient, context: { index: number; canvas?: HTMLCanvasElement }) => onClientCreated(client, { ...context, index }));
      }
      shifted.onClientCreated = created.get(onClientCreated);
    }
    if (joinGame) {
      const { options, initialAreas } = joinGame;
      shifted.joinGame = {
        ...joinGame,
        ...(typeof options === 'function' ? { options: (context: { index: number; auth: unknown }) => options({ ...context, index }) } : {}),
        ...(typeof initialAreas === 'function' ? { initialAreas: (context: { index: number; auth: unknown }) => initialAreas({ ...context, index }) } : {}),
      };
    }
    return shifted;
  };
  const config = selection.clientConfigDefaults !== selection.clientConfig ? selection.clientConfigDefaults : undefined;
  return {
    definition: { ...atIndex(selection.clientConfig), numberOfClients: 1, domSetup: () => surface() },
    config: config && atIndex(config),
  };
}
